import { createContext, useContext, useState, useCallback, useEffect } from "react";

const IngredientSessionContext = createContext(null);
const MAX_SESSION_INGREDIENTS = 10;
const STORAGE_KEY = "ingredientSession";

function restoreFromStorage() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (item) =>
          item &&
          typeof item.uri === "string" &&
          item.uri.length > 0 &&
          typeof item.name === "string" &&
          item.name.length > 0
      )
      .slice(0, MAX_SESSION_INGREDIENTS);
  } catch {
    return [];
  }
}

export function IngredientSessionProvider({ children }) {
  const [sessionIngredients, setSessionIngredients] = useState(restoreFromStorage);

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(sessionIngredients));
  }, [sessionIngredients]);

  const addIngredient = useCallback((ingredient) => {
    setSessionIngredients((prev) => {
      if (prev.length >= MAX_SESSION_INGREDIENTS) return prev;
      if (prev.some((ing) => ing.uri === ingredient.uri)) return prev;
      return [...prev, ingredient];
    });
  }, []);

  const removeIngredient = useCallback((uri) => {
    setSessionIngredients((prev) => prev.filter((ing) => ing.uri !== uri));
  }, []);

  const clearSession = useCallback(() => {
    setSessionIngredients([]);
  }, []);

  const isInSession = useCallback(
    (uri) => sessionIngredients.some((ing) => ing.uri === uri),
    [sessionIngredients]
  );

  const count = sessionIngredients.length;
  const isFull = count >= MAX_SESSION_INGREDIENTS;

  return (
    <IngredientSessionContext.Provider
      value={{
        sessionIngredients,
        addIngredient,
        removeIngredient,
        clearSession,
        isInSession,
        count,
        isFull,
        MAX_SESSION_INGREDIENTS,
      }}
    >
      {children}
    </IngredientSessionContext.Provider>
  );
}

export function useIngredientSession() {
  const context = useContext(IngredientSessionContext);
  if (!context) {
    throw new Error(
      "useIngredientSession must be used within an IngredientSessionProvider"
    );
  }
  return context;
}
